export const TA_EGO_KEYS = ["parent", "adult", "child"];

export function buildTaEgoReport({
  scores = {},
  participant = {},
  language = "tr"
}) {
  const lang = language === "en" ? "en" : "tr";
  const normalized = normalizeScores(scores);

  const sorted = TA_EGO_KEYS
    .map((key) => ({ key, value: normalized[key] }))
    .sort((a, b) => b.value - a.value);

  const dominant = sorted[0]?.key || "adult";
  const secondary = sorted[1]?.key || "parent";
  const lowest = sorted[sorted.length - 1]?.key || "child";
  const spread = Math.round((sorted[0]?.value || 0) - (sorted[sorted.length - 1]?.value || 0));
  const balanceType = getBalanceType({ scores: normalized, dominant, secondary, lowest, spread, lang });

  const report = {
    language: lang,
    participant,
    scores: normalized,
    dominant,
    secondary,
    lowest,
    dominantLabel: getEgoStateName(dominant, lang),
    secondaryLabel: getEgoStateName(secondary, lang),
    lowestLabel: getEgoStateName(lowest, lang),
    spread,
    balanceType,
    title: lang === "tr" ? "Someren Labs TA / 3 Ego Kişisel Raporu" : "Someren Labs TA / 3 Ego Personal Report",
    executiveSummary: buildExecutiveSummary({ scores: normalized, dominant, secondary, lowest, spread, balanceType, lang }),
    egoBalanceInsight: buildEgoBalanceInsight({ scores: normalized, dominant, secondary, lowest, spread, lang }),
    workStyle: buildWorkStyle({ dominant, secondary, lang }),
    strengths: buildStrengths({ dominant, secondary, lang }),
    watchouts: buildWatchouts({ dominant, lowest, scores: normalized, lang }),
    communicationTips: buildCommunicationTips({ dominant, secondary, lowest, lang }),
    managerNotes: buildManagerNotes({ dominant, secondary, lowest, lang }),
    hrNotes: buildHrNotes({ dominant, secondary, lowest, lang }),
    developmentPlan: buildDevelopmentPlan({ dominant, lowest, lang }),
    sections: TA_EGO_KEYS.map((key) => ({
      key,
      label: getEgoStateName(key, lang),
      value: normalized[key],
      level: getScoreLevel(normalized[key]),
      content: getStateContent(key, normalized[key], dominant, lowest, lang)
    }))
  };

  return report;
}

export function buildTaEgoReportSummary({ scores = {}, participant = {}, language = "tr" }) {
  const report = buildTaEgoReport({ scores, participant, language });

  return {
    language: report.language,
    dominantState: report.dominant,
    secondaryState: report.secondary,
    lowestState: report.lowest,
    dominantLabel: report.dominantLabel,
    secondaryLabel: report.secondaryLabel,
    lowestLabel: report.lowestLabel,
    scores: report.scores,
    balanceType: report.balanceType,
    spread: report.spread,
    executiveSummary: report.executiveSummary,
    egoBalanceInsight: report.egoBalanceInsight,
    riskSignal: report.watchouts?.[0] || "",
    developmentFocus: report.developmentPlan?.[0] || "",
    generatedAt: new Date().toISOString()
  };
}

function normalizeScores(scores = {}) {
  return {
    parent: clampScore(scores.parentPercent ?? scores.parent),
    adult: clampScore(scores.adultPercent ?? scores.adult),
    child: clampScore(scores.childPercent ?? scores.child)
  };
}

function clampScore(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(100, Math.round(number)));
}

export function getScoreLevel(score) {
  if (score >= 75) return "high";
  if (score >= 50) return "medium";
  return "low";
}

export function getEgoStateName(key, lang = "tr") {
  const names = {
    tr: {
      parent: "Ebeveyn Ego",
      adult: "Yetişkin Ego",
      child: "Çocuk Ego"
    },
    en: {
      parent: "Parent Ego",
      adult: "Adult Ego",
      child: "Child Ego"
    }
  };

  return names[lang]?.[key] || key;
}

function getBalanceType({ scores, dominant, spread, lang }) {
  const adultStrong = scores.adult >= 60;
  const parentStrong = scores.parent >= 60;
  const childStrong = scores.child >= 60;
  const adultLow = scores.adult < 50;

  if (lang === "en") {
    if (parentStrong && childStrong && adultLow) return "High Parent-Child tension with lower Adult mediation";
    if (adultStrong && spread <= 30) return "Adult-led balanced profile";
    if (dominant === "adult") return "Adult-led analytical decision profile";
    if (dominant === "parent" && adultStrong) return "Structured and responsible profile supported by Adult Ego";
    if (dominant === "parent") return "Parent-led structured responsibility profile";
    if (dominant === "child" && adultStrong) return "Creative and expressive profile supported by Adult Ego";
    if (dominant === "child") return "Child-led expressive and spontaneous profile";
    return "Mixed ego state profile";
  }

  if (parentStrong && childStrong && adultLow) return "Yetişkin aracılığı düşük, Ebeveyn-Çocuk gerilimi yüksek profil";
  if (adultStrong && spread <= 30) return "Yetişkin merkezli dengeli profil";
  if (dominant === "adult") return "Yetişkin merkezli analitik karar profili";
  if (dominant === "parent" && adultStrong) return "Yetişkin Ego ile desteklenen yapı ve sorumluluk profili";
  if (dominant === "parent") return "Ebeveyn merkezli yapı ve sorumluluk profili";
  if (dominant === "child" && adultStrong) return "Yetişkin Ego ile desteklenen yaratıcı ve ifadeci profil";
  if (dominant === "child") return "Çocuk merkezli ifadeci ve spontan profil";
  return "Karma ego durumu profili";
}

function buildExecutiveSummary({ dominant, secondary, lowest, balanceType, lang }) {
  if (lang === "en") {
    return `This profile is led by ${getEgoStateName(dominant, "en")} with ${getEgoStateName(secondary, "en")} as the secondary signal. The overall pattern can be read as: ${balanceType}. The lower ${getEgoStateName(lowest, "en")} signal should not be treated as a weakness label; it is a development and balance indicator. The report shows which internal position the participant may use more visibly while communicating, deciding and responding to pressure.`;
  }

  return `Bu profilde baskın ego durumu ${getEgoStateName(dominant, "tr")}; ikincil sinyal ise ${getEgoStateName(secondary, "tr")} olarak görünmektedir. Genel örüntü şu şekilde okunabilir: ${balanceType}. Daha düşük görünen ${getEgoStateName(lowest, "tr")} bir “zayıflık etiketi” değil; gelişim ve denge göstergesi olarak değerlendirilmelidir. Bu rapor, katılımcının iletişim kurarken, karar verirken ve baskıya yanıt verirken hangi iç pozisyonu daha görünür kullandığını anlamaya yardımcı olur.`;
}

function buildEgoBalanceInsight({ scores, dominant, secondary, lowest, spread, lang }) {
  if (lang === "en") {
    return `The distance between the highest and lowest ego state is ${spread} points. ${getEgoStateName(dominant, "en")} is the most visible state, while ${getEgoStateName(lowest, "en")} may require more conscious access depending on the situation. A healthy profile is not about maximising one ego state; it is about using Parent, Adult and Child Ego states with awareness and flexibility.`;
  }

  return `En yüksek ve en düşük ego durumu arasındaki fark ${spread} puandır. ${getEgoStateName(dominant, "tr")} en görünür alan olarak öne çıkarken, ${getEgoStateName(lowest, "tr")} bazı durumlarda daha bilinçli erişim gerektirebilir. Sağlıklı profil, tek bir ego durumunu en yükseğe çıkarmak değil; Ebeveyn, Yetişkin ve Çocuk Ego durumlarını farkındalık ve esneklikle kullanabilmektir.`;
}

function buildWorkStyle({ dominant, secondary, lang }) {
  const tr = {
    parent: "Kural, sorumluluk, standart, koruma ve yön verme ihtiyacı belirgindir.",
    adult: "Veri, gerçeklik, analiz, sakin değerlendirme ve rasyonel karar eğilimi belirgindir.",
    child: "Yaratıcılık, spontanlık, duygu, enerji ve doğrudan ifade ihtiyacı belirgindir."
  };

  const en = {
    parent: "Rules, responsibility, standards, protection and direction are visible behavioural needs.",
    adult: "Data, reality-testing, analysis, calm evaluation and rational decision-making are visible tendencies.",
    child: "Creativity, spontaneity, emotion, energy and direct expression are visible behavioural needs."
  };

  const source = lang === "en" ? en : tr;

  if (lang === "en") {
    return `${source[dominant]} The secondary ${getEgoStateName(secondary, "en")} signal adds another layer to the participant’s work style: ${source[secondary]}`;
  }

  return `${source[dominant]} İkincil ${getEgoStateName(secondary, "tr")} sinyali çalışma tarzına ek bir katman ekler: ${source[secondary]}`;
}

function buildStrengths({ dominant, secondary, lang }) {
  const tr = {
    parent: "Sorumluluk alma, standart koyma ve koruyucu liderlik",
    adult: "Veriyle düşünme, gerçekçi değerlendirme ve sakin karar alma",
    child: "Yaratıcılık, enerji, doğrudan ifade ve yenilik açıklığı"
  };

  const en = {
    parent: "Taking responsibility, setting standards and protective leadership",
    adult: "Thinking with data, realistic evaluation and calm decision-making",
    child: "Creativity, energy, direct expression and openness to novelty"
  };

  const source = lang === "en" ? en : tr;

  return [
    source[dominant],
    source[secondary],
    lang === "en"
      ? "Potential to make internal decision patterns more visible and manageable"
      : "İç karar örüntülerini daha görünür ve yönetilebilir hale getirme potansiyeli"
  ];
}

function buildWatchouts({ dominant, lowest, scores, lang }) {
  const tr = {
    parent: "Ebeveyn Ego yükseldiğinde kişi fazla kuralcı, eleştirel veya kontrolcü algılanabilir.",
    adult: "Yetişkin Ego yükseldiğinde kişi fazla rasyonel, mesafeli veya duygusal sinyallere kapalı algılanabilir.",
    child: "Çocuk Ego yükseldiğinde kişi fazla tepkisel, dağınık veya duygusal algılanabilir."
  };

  const en = {
    parent: "When Parent Ego is high, the person may be perceived as overly rule-driven, critical or controlling.",
    adult: "When Adult Ego is high, the person may be perceived as overly rational, distant or less open to emotional signals.",
    child: "When Child Ego is high, the person may be perceived as reactive, scattered or overly emotional."
  };

  const lowTr = {
    parent: "Düşük Ebeveyn Ego; sınır koyma, standart oluşturma veya sorumluluk çerçevesi kurma alanında gelişim ihtiyacına işaret edebilir.",
    adult: "Düşük Yetişkin Ego; karar kalitesinde duygu, alışkanlık, otorite veya refleks etkisinin artabileceğini gösterebilir.",
    child: "Düşük Çocuk Ego; yaratıcılık, spontanlık, neşe, esneklik ve duygusal ifade alanlarının geri planda kalabileceğini gösterebilir."
  };

  const lowEn = {
    parent: "Lower Parent Ego may indicate a development need around boundaries, standards or responsibility frames.",
    adult: "Lower Adult Ego may indicate that emotion, habit, authority or reflex may influence decision quality more strongly.",
    child: "Lower Child Ego may indicate that creativity, spontaneity, joy, flexibility and emotional expression may stay in the background."
  };

  const main = lang === "en" ? en : tr;
  const low = lang === "en" ? lowEn : lowTr;

  return [main[dominant], low[lowest]];
}

function buildCommunicationTips({ dominant, secondary, lowest, lang }) {
  if (lang === "en") {
    return [
      `Use your ${getEgoStateName(dominant, "en")} signal consciously; make the intention behind your communication visible.`,
      `Balance it with ${getEgoStateName(secondary, "en")} so that your impact does not become one-dimensional.`,
      `Before responding from ${getEgoStateName(dominant, "en")}, check whether the moment needs structure, data, empathy, creativity or reassurance.`,
      `Create deliberate space for ${getEgoStateName(lowest, "en")} when the situation requires it.`
    ];
  }

  return [
    `${getEgoStateName(dominant, "tr")} sinyalinizi bilinçli kullanın; iletişiminizin arkasındaki niyeti görünür kılın.`,
    `Etkinizin tek boyutlu kalmaması için bunu ${getEgoStateName(secondary, "tr")} ile dengeleyin.`,
    `${getEgoStateName(dominant, "tr")} üzerinden yanıt vermeden önce, o anın yapı, veri, empati, yaratıcılık veya güvence ihtiyacını kontrol edin.`,
    `Durum gerektirdiğinde ${getEgoStateName(lowest, "tr")} alanına bilinçli olarak yer açın.`
  ];
}

function buildManagerNotes({ dominant, secondary, lowest, lang }) {
  if (lang === "en") {
    return [
      `Give this person room to use their ${getEgoStateName(dominant, "en")} strength, but clarify the expected output and decision frame.`,
      `The secondary ${getEgoStateName(secondary, "en")} signal can be used as a development bridge.`,
      `The lower ${getEgoStateName(lowest, "en")} signal may guide coaching conversations around flexibility, boundaries, creativity or decision quality.`
    ];
  }

  return [
    `Bu kişiye ${getEgoStateName(dominant, "tr")} gücünü kullanabileceği alan açın; ancak beklenen çıktı ve karar çerçevesini netleştirin.`,
    `İkincil ${getEgoStateName(secondary, "tr")} sinyali gelişim köprüsü olarak kullanılabilir.`,
    `Daha düşük ${getEgoStateName(lowest, "tr")} sinyali; esneklik, sınır koyma, yaratıcılık veya karar kalitesi etrafındaki koçluk görüşmelerine yön verebilir.`
  ];
}

function buildHrNotes({ dominant, secondary, lowest, lang }) {
  if (lang === "en") {
    return [
      "This profile can be used in coaching, leadership development, communication awareness and team balance conversations; it should not be used as a clinical diagnosis or standalone selection tool.",
      `The ${getEgoStateName(dominant, "en")} - ${getEgoStateName(secondary, "en")} pattern may indicate the participant’s visible decision and interaction style under work conditions.`,
      `The lower ${getEgoStateName(lowest, "en")} signal can support development planning and team complementarity discussions.`
    ];
  }

  return [
    "Bu profil koçluk, liderlik gelişimi, iletişim farkındalığı ve ekip dengesi görüşmelerinde kullanılabilir; klinik teşhis veya tek başına seçim aracı gibi kullanılmamalıdır.",
    `${getEgoStateName(dominant, "tr")} - ${getEgoStateName(secondary, "tr")} örüntüsü, katılımcının iş koşullarında görünür karar ve etkileşim tarzını gösterebilir.`,
    `Daha düşük ${getEgoStateName(lowest, "tr")} sinyali, gelişim planı ve takım tamamlayıcılığı görüşmeleri için kullanılabilir.`
  ];
}

function buildDevelopmentPlan({ dominant, lowest, lang }) {
  if (lang === "en") {
    return [
      `Week 1: Observe when you respond from ${getEgoStateName(dominant, "en")} and what effect it creates.`,
      "Week 2: Ask for one short feedback about how your communication style is experienced by others.",
      `Week 3: Practise one behaviour linked to your lower ${getEgoStateName(lowest, "en")} signal.`,
      "Week 4: Review one real work interaction and decide what to repeat, reduce or rebalance."
    ];
  }

  return [
    `1. Hafta: Hangi anlarda ${getEgoStateName(dominant, "tr")} üzerinden yanıt verdiğinizi ve bunun nasıl bir etki yarattığını gözlemleyin.`,
    "2. Hafta: İletişim tarzınızın başkaları tarafından nasıl deneyimlendiğine dair kısa bir geri bildirim isteyin.",
    `3. Hafta: Daha düşük ${getEgoStateName(lowest, "tr")} sinyalinizle bağlantılı bir davranışı bilinçli olarak uygulayın.`,
    "4. Hafta: Gerçek bir iş etkileşimini gözden geçirip neyi tekrar edeceğinizi, neyi azaltacağınızı ve neyi dengeleyeceğinizi belirleyin."
  ];
}

function getStateContent(key, score, dominant, lowest, lang) {
  const level = getScoreLevel(score);

  const tr = {
    parent: {
      title: "Ebeveyn Ego",
      high: "Ebeveyn Ego yüksek olduğunda kişi sorumluluk, standart, koruma, yön verme ve gerektiğinde eleştirel değerlendirme alanlarını daha görünür kullanabilir.",
      medium: "Ebeveyn Ego orta seviyede olduğunda kişi kuralları ve sorumlulukları kullanır; ancak bunları her durumda baskın davranış pozisyonu haline getirmeyebilir.",
      low: "Ebeveyn Ego düşük olduğunda sınır koyma, standart belirleme veya otorite kullanma alanları daha bilinçli geliştirilmesi gereken alanlar olabilir."
    },
    adult: {
      title: "Yetişkin Ego",
      high: "Yetişkin Ego yüksek olduğunda kişi veriyi, gerçekliği, sakin değerlendirmeyi ve mantıklı karar vermeyi daha görünür kullanabilir.",
      medium: "Yetişkin Ego orta seviyede olduğunda kişi rasyonel değerlendirme yapabilir; ancak baskı altında duygu, alışkanlık veya otorite etkisi artabilir.",
      low: "Yetişkin Ego düşük olduğunda karar kalitesinde refleks, duygu, eski alışkanlıklar veya dış otorite etkisi daha fazla görülebilir."
    },
    child: {
      title: "Çocuk Ego",
      high: "Çocuk Ego yüksek olduğunda kişi yaratıcılık, enerji, spontanlık, duygusal ifade ve yenilik açıklığını daha görünür kullanabilir.",
      medium: "Çocuk Ego orta seviyede olduğunda kişi spontanlık ve duygusal ifadeyi kullanır; ancak bunu kontrollü veya bağlama göre sınırlı biçimde gösterebilir.",
      low: "Çocuk Ego düşük olduğunda yaratıcılık, oyun alanı, esneklik, canlılık ve doğrudan duygu ifadesi geri planda kalabilir."
    }
  };

  const en = {
    parent: {
      title: "Parent Ego",
      high: "When Parent Ego is high, the person may use responsibility, standards, protection, direction and critical evaluation more visibly.",
      medium: "When Parent Ego is medium, the person uses rules and responsibilities, but may not make them the dominant behavioural position in every situation.",
      low: "When Parent Ego is low, boundaries, standards and use of authority may require more conscious development."
    },
    adult: {
      title: "Adult Ego",
      high: "When Adult Ego is high, the person may use data, reality-testing, calm evaluation and rational decision-making more visibly.",
      medium: "When Adult Ego is medium, the person can evaluate rationally, but pressure may increase the influence of emotion, habit or authority.",
      low: "When Adult Ego is low, decision quality may be influenced more by reflex, emotion, old habits or external authority."
    },
    child: {
      title: "Child Ego",
      high: "When Child Ego is high, the person may use creativity, energy, spontaneity, emotional expression and openness to novelty more visibly.",
      medium: "When Child Ego is medium, the person uses spontaneity and emotional expression, but may show it in a controlled or context-dependent way.",
      low: "When Child Ego is low, creativity, playfulness, flexibility, vitality and direct emotional expression may stay in the background."
    }
  };

  const source = lang === "en" ? en : tr;
  const content = source[key];
  const text = content[level] || content.medium;

  return {
    title: content.title,
    headline:
      lang === "en"
        ? `${content.title} is ${level} in this profile.`
        : `${content.title} bu profilde ${level === "high" ? "yüksek" : level === "medium" ? "orta" : "düşük"} seviyededir.`,
    interpretation: text,
    role:
      key === dominant
        ? lang === "en"
          ? "This is the most visible ego state in the profile."
          : "Bu, profilde en görünür ego durumudur."
        : key === lowest
          ? lang === "en"
            ? "This is the least visible ego state in the profile."
            : "Bu, profilde en düşük görünen ego durumudur."
          : lang === "en"
            ? "This ego state adds a secondary balancing layer."
            : "Bu ego durumu profile dengeleyici ikincil bir katman ekler."
  };
}
